// Copyright: IQGeo Limited 2010-2019
import _ from 'underscore';

function DBPredicate(token, operands) {
    // A node from a filter parse tree
    this.type = token['type'];
    this.value = token['value'];
    this.operands = operands;
}

DBPredicate.prototype.treeStr = function(indent) {
    if (!indent) indent = '';

    let lines = `${indent + this.type} ${this.value}\n`;
    indent += '  ';
    _.each(this.operands, op => {
        if ('treeStr' in op) {
            lines += op.treeStr(indent);
        }
    });

    return lines;
};

DBPredicate.prototype.fieldNames = function() {
    const names = this._fieldNames();
    names.sort();
    return _.uniq(names);
};

DBPredicate.prototype._fieldNames = function() {
    let names = [];

    if (this.type == 'field') names.push(this.value);

    _.each(this.operands, operand => {
        names = names.concat(operand._fieldNames());
    });

    return names;
};

DBPredicate.prototype.sqlFilter = function(table, fieldMap, variableMap, dialect) {
    // Returns SQL select clause for applying self on TABLE
    //
    // VARIABLEMAP is a dict of session variable values. DIALECT defines the dialect to generate, one of:
    //  'SQL'   SQLITE SQL
    //  'CQL'   Geoserver CQL
    //
    // Optional FIELD_MAP is a mapping from the columns of TABLE to a separate target table (e.g. pole -> geom_world_point).
    // If provided, the resulting query is for the target table

    if (this.type == 'unary_op')
        return this._sqlUnaryOpFilter(table, this.operands[0], fieldMap, variableMap, dialect);
    if (this.type == 'join_op')
        return this._sqlJoinOpFilter(
            table,
            this.operands[0],
            this.operands[1],
            fieldMap,
            variableMap,
            dialect
        );
    if (this.type == 'comp_op')
        return this._sqlCompOpFilter(
            table,
            this.operands[0],
            this.operands[1],
            fieldMap,
            variableMap,
            dialect
        );
    if (this.type == 'func_op')
        return this._sqlInOpFilter(
            table,
            this.operands[0],
            this.operands[1],
            fieldMap,
            variableMap,
            dialect
        );
    if (this.type == 'bool_const') return this._asSqlOperand(table, fieldMap, variableMap, dialect);

    throw Error(`Unknown parse node type: ${node.type}`);
};

DBPredicate.prototype._sqlUnaryOpFilter = function(
    table,
    operand1,
    fieldMap,
    variableMap,
    dialect
) {
    // SQL select clause for a unary_op parse node

    if (this.value == 'not')
        return `NOT ${operand1.sqlFilter(table, fieldMap, variableMap, dialect)}`;

    throw Error(`Unknown unary_op type: ${this.value}`);
};

DBPredicate.prototype._sqlJoinOpFilter = function(
    table,
    operand1,
    operand2,
    fieldMap,
    variableMap,
    dialect
) {
    // SQL select clause for a join_op parse node

    const sqlOp1 = operand1.sqlFilter(table, fieldMap, variableMap, dialect);
    const sqlOp2 = operand2.sqlFilter(table, fieldMap, variableMap, dialect);

    if (this.value == '&') return `( ${sqlOp1} AND ${sqlOp2} )`;
    if (this.value == '|') return `( ${sqlOp1} OR ${sqlOp2} )`;

    throw Error(`Unknown join_op type: ${this.value}`);
};

DBPredicate.prototype._sqlCompOpFilter = function(
    table,
    operand1,
    operand2,
    fieldMap,
    variableMap,
    dialect
) {
    // SQL select clause for a comparison op parse node

    const sql_op1 = operand1._asSqlOperand(table, fieldMap, variableMap, dialect);
    const sql_op2 = operand2._asSqlOperand(table, fieldMap, variableMap, dialect);
    let comp_op = this.value;

    if (_.isEqual(comp_op, 'ilike')) {
        if (dialect == 'CQL') {
            return `( strToLowerCase(${sql_op1}) LIKE(${sql_op2}) )`;
        } else {
            return `( lower(${sql_op1}) LIKE lower(${sql_op2} ) ESCAPE "\\" )`; //Escape wildCards for sqlite
        }
    }
    if (dialect != 'CQL') {
        //Escape wildCards for sqlite
        if (_.isEqual(comp_op, 'like')) {
            return `( ${sql_op1} ${comp_op} ${sql_op2} ESCAPE "\\"  )`;
        }
    }
    if (_.isEqual(comp_op, '=') && (this.isNull(operand1) || this.isNull(operand2)))
        comp_op = ' IS ';
    if (_.isEqual(comp_op, '<>') && (this.isNull(operand1) || this.isNull(operand2)))
        comp_op = ' IS NOT ';

    return `( ${sql_op1} ${comp_op} ${sql_op2} )`;
};

DBPredicate.prototype._sqlInOpFilter = (
    table,
    operand1,
    operand2,
    fieldMap,
    variableMap,
    dialect
) => {
    // SQL select clause for a in() operator parse node

    const sql_op1 = operand1._asSqlOperand(table, fieldMap, variableMap, dialect);
    let sqlArgList = '';
    let sep = '';

    _.each(operand2.operands, op => {
        if (op.type == 'variable' && Array.isArray(op.variableValue(variableMap))) {
            // Case: Session variable set to list
            _.each(op.variableValue(variableMap), var_el => {
                sqlArgList += `${sep}'${var_el}'`;
                sep = ',';
            });
        } else {
            // Case: Everything else
            sqlArgList += sep + op._asSqlOperand(table, fieldMap, variableMap, dialect);
            sep = ',';
        }
    });

    return `( ${sql_op1} IN (${sqlArgList}) )`;
};

DBPredicate.prototype._asSqlOperand = function(table, fieldMap, variableMap, dialect) {
    // SQL select element for a literal, field or session variable reference

    let bool_consts = {};
    if (dialect == 'CQL') {
        bool_consts = { true: 'TRUE', false: 'FALSE' };
    } else {
        bool_consts = { true: '1', false: '0' };
    }

    switch (this.type) {
        case 'str_const':
            return `'${this.value}'`;

        case 'bool_const':
            return bool_consts[this.value];
            break;

        case 'field':
            if (fieldMap) {
                if (fieldMap[this.value].sqlType)
                    return this._sqlCast(fieldMap[this.value].target, fieldMap[this.value].sqlType);
                else return fieldMap[this.value].target;
            } else return `"${this.value}"`;
            break;

        case 'variable':
            //ENH: move this check inside the actual method
            if (this.variableValue(variableMap) === undefined) return 'NULL';
            else return `'${this.variableValue(variableMap)}'`;

        case 'named_const':
            if (this.value === null) return 'NULL';
            return this.value;
            break;

        default:
            return this.value;
    }
};

/** ==============================================================================
 *                                MATCHING
 *================================================================================
 */
DBPredicate.prototype.matches = function(record, sessionVars) {
    if (this.type == 'unary_op')
        return this._evaluateUnaryOp(record, this.operands[0], sessionVars);
    if (this.type == 'join_op')
        return this._evaluateJoinOp(record, this.operands[0], this.operands[1], sessionVars);
    if (this.type == 'comp_op')
        return this._evaluateCompOp(record, this.operands[0], this.operands[1], sessionVars);
    if (this.type == 'func_op')
        return this._evaluateInOp(record, this.operands[0], this.operands[1], sessionVars);
    if (this.type == 'bool_const') return this.value;

    throw Error(`Unknown parse node type: ${node.type}`);
};

DBPredicate.prototype._evaluateUnaryOp = function(record, operand1, sessionVars) {
    if (this.value == 'not') return !operand1.matches(record, sessionVars);

    throw Error(`Unknown parse node type: ${node.type}`);
};

DBPredicate.prototype._evaluateJoinOp = function(record, operand1, operand2, sessionVars) {
    const sqlOp1 = operand1.matches(record, sessionVars);
    const sqlOp2 = operand2.matches(record, sessionVars);

    if (this.value == '&') return sqlOp1 && sqlOp2;
    if (this.value == '|') return sqlOp1 || sqlOp2;

    throw Error(`Unknown join_op type: ${this.value}`);
};

(DBPredicate.prototype._evaluateCompOp = function(record, operand1, operand2, sessionVars) {
    const op1 = operand1._evaluateOperand(record, sessionVars);
    const op2 = operand2._evaluateOperand(record, sessionVars);
    const compOp = this.value;

    if (compOp == '=') return op1 == op2;
    if (compOp == '<>') return op1 != op2;
    if (compOp == '<=') return op1 <= op2;
    if (compOp == '>=') return op1 >= op2;
    if (compOp == '<') return op1 < op2;
    if (compOp == '>') return op1 > op2;
    if (compOp == 'like') return this._eavluateStrLike(op1, op2, true);
    if (compOp == 'ilike') return this._eavluateStrLike(op1, op2, false);

    throw Error(`Unknown comparison parse node type: ${node.type}`);
}),
    (DBPredicate.prototype._eavluateStrLike = (str, sqlPattern, caseSensitive) => {
        /**
         * True if STR matches SQL_PATTERN
         * SQL_PATTERN is a SQL style 'like' pattern (special chars %, _ and \)
         */

        const rePattern = DBPredicate.prototype._regexFor(sqlPattern);
        let flag = 'm';
        if (!caseSensitive) flag += 'i';
        const regex = new RegExp(rePattern, flag);

        if (!str) str = '';
        return !!str.match(regex);
    }),
    (DBPredicate.prototype._evaluateInOp = (record, operand1, operand2, sessionVars) => {
        // Evaluate 'in' operator on REC

        //Get arg to perform in test on
        const op1 = operand1._evaluateOperand(record, sessionVars);

        //Build args to in(), expanding session variables (where necessary)
        let value;
        let values = [];
        _.each(operand2.operands, operand => {
            value = operand.variableValue(sessionVars);
            if (Array.isArray(value)) values = values.concat(value);
            else values.push(operand._evaluateOperand(record, sessionVars));
        });

        return _.contains(values, op1);
    }),
    (DBPredicate.prototype._evaluateOperand = function(record, sessionVars) {
        //Self's value as an expression operand on REC
        let value;
        if (this.type == 'field') return record.properties[this.value];

        if (this.type == 'variable') {
            value = this.variableValue(sessionVars);
            //ENH: move this check inside the actual method
            if (value === undefined) return null;
            else return value;
        }

        if (this.type.endsWith('const')) return this.value;

        throw Error(`Not an operand:${this.type}`);
    }),
    /**
     * ==============================================================================
     *                              HELPERS
     * ==============================================================================
     */

    (DBPredicate.prototype._regexFor = sqlPattern => {
        /**
         * The python regex pattern equivalent to SQL_PATTERN
         *
         * SQL_PATTERN can contain the following wildcards:
         * %  Any number of any char
         * _  Exactly one char
         *
         * The character '\' can be used to escape a wildcard
         */

        const wildcardReps = {
            _: '.', // Convert sql wildcard -> regex wildcard
            '%': '.*'
        };

        const specialCharReps = {
            '.': '\\.', // Escape regex special chars
            '*': '\\*',
            '^': '\\^',
            $: '\\$',
            '+': '\\+',
            '?': '\\?',
            '{': '\\{',
            '}': '\\}',
            '[': '\\[',
            ']': '\\]',
            '(': '\\(',
            ')': '\\)',
            '|': '\\|'
        };

        let rePattern = '';
        let escaping = false;
        let i;
        for (i = 0; i < sqlPattern.length; i++) {
            if (escaping) {
                rePattern += specialCharReps[sqlPattern[i]] || sqlPattern[i]; //Case: Previous char was '\'
                escaping = false;
            } else if (sqlPattern[i] == '\\') escaping = true;
            // Case: This char is SQL escape
            else if (_.contains(_.keys(wildcardReps), sqlPattern[i])) {
                rePattern += wildcardReps[sqlPattern[i]]; // Case: This char is SQL wildcard
            } else {
                rePattern += sqlPattern[i]; // Case: This char is SQL literal
            }
        }
        return rePattern;
    }),
    (DBPredicate.prototype.variableValue = function(variableMap) {
        // Get value of a session variable node (handling default)
        let name, defaultValue, value;
        const parts = this.value.split(':');

        name = parts[0];
        if (parts.length > 1) {
            parts.shift();
            defaultValue = parts.join(':');
        } else {
            defaultValue = undefined;
        }

        if (variableMap && name in variableMap) value = variableMap[name];
        else value = defaultValue;

        return value;
    });

DBPredicate.prototype._sqlCast = (field, type) => `cast( ${field} as ${type} ) `;

DBPredicate.prototype.isNull = node => _.isEqual(node.type, 'named_const') && node.value === null;

export default DBPredicate;
